import { IDEA_STATUSES } from '@cult/domain';
import type { PublicIdea } from '@cult/schemas';
import { Link, useNavigate } from '@tanstack/react-router';
import { Flex, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { CategoryTag, FeedCard, UserAvatar } from '../../../../components';
import { stripMarkdown } from '../../../../lib/markdown';
import { IdeaStatusTag } from '../IdeaStatusTag/IdeaStatusTag';

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;

type Props = {
  idea: PublicIdea;
  index?: number;
};

export const IdeaRow = ({ idea, index = 0 }: Props) => {
  const navigate = useNavigate();

  const isTerminal = idea.status === IDEA_STATUSES.CLOSED;

  return (
    <FeedCard accent={idea.category.color ?? undefined} index={index} dimmed={isTerminal}>
      {/* Whole card navigates to detail page */}
      <div
        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
        onClick={() => navigate({ to: '/ideas/$id', params: { id: String(idea.number) } })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') navigate({ to: '/ideas/$id', params: { id: String(idea.number) } });
        }}
      >
        {/* Row 1: title (hero) pinned-left + status tag pinned-right */}
        <Flex align="flex-start" justify="space-between" gap={10}>
          <Link
            to="/ideas/$id"
            params={{ id: String(idea.number) }}
            style={{ color: 'inherit', textDecoration: 'none', flex: 1, minWidth: 0 }}
          >
            <Text strong style={{ fontSize: 15, lineHeight: 1.45, display: 'block' }}>
              {idea.title}
            </Text>
          </Link>
          <Flex align="center" gap={6} style={{ flexShrink: 0, paddingTop: 1 }}>
            <IdeaStatusTag status={idea.status} />
          </Flex>
        </Flex>

        {/* Row 2: body excerpt — 2 lines */}
        <Paragraph
          type="secondary"
          ellipsis={{ rows: 2 }}
          style={{ margin: '5px 0 7px', fontSize: 14, lineHeight: 1.55 }}
        >
          {stripMarkdown(idea.body)}
        </Paragraph>

        {/* Row 3: meta — number · author · time · closedAt · decision note */}
        <Space size={8} wrap align="center">
          <Text type="secondary" style={{ fontSize: 12 }}>
            #{idea.number}
          </Text>
          {idea.author ? (
            <>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ·
              </Text>
              <Space size={4} align="center">
                <UserAvatar size={16} avatarUrl={idea.author.avatarUrl} style={{ flexShrink: 0 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {idea.author.displayName}
                </Text>
              </Space>
            </>
          ) : null}
          <Text type="secondary" style={{ fontSize: 12 }}>
            ·
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(idea.createdAt).fromNow()}
          </Text>
          {idea.closedAt ? (
            <>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ·
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                closed {dayjs(idea.closedAt).format('MMM D')}
              </Text>
            </>
          ) : null}
        </Space>

        {/* Row 4: category */}
        <Flex style={{ marginTop: 8 }}>
          <CategoryTag category={idea.category} />
        </Flex>
      </div>
    </FeedCard>
  );
};
